/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Coins, ShoppingCart, Lock } from "lucide-react";

export default function ShopPage() {
  const [shop, setShop] = useState({ money: 0, items: [] });
  const [qtys, setQtys] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/shop");
      setShop(data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  const buy = async (item_id) => {
    const qty = qtys[item_id] || 1;
    setBusy(true);
    try {
      await api.post("/shop/buy", { item_id, qty });
      toast.success(`Acheté : ${qty} × ${shop.items.find(i => i.item_id === item_id).name}`);
      setQtys({ ...qtys, [item_id]: 1 });
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="shop-page">
      <div className="flex justify-between items-end mb-4 flex-wrap gap-3">
        <div>
          <div className="overline">Comptoir du labo</div>
          <h1 className="font-serif-display text-4xl md:text-5xl leading-none mt-1">Boutique</h1>
          <p className="text-xs text-[#4A4A4A] mt-2 max-w-2xl">
            Vos dresseurs rapportent de l&apos;argent à chaque combat gagné en exploration et à chaque badge décroché.
          </p>
        </div>
        <div className="panel-strong px-4 py-2 flex items-center gap-2" data-testid="shop-money">
          <Coins size={20} className="text-[#B87333]"/>
          <div>
            <div className="overline text-[9px]">Trésor du labo</div>
            <div className="font-serif-display text-3xl leading-none">{shop.money} ₽</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="shop-grid">
        {shop.items.map((it) => {
          const qty = qtys[it.item_id] || 1;
          const total = it.price * qty;
          const canAfford = shop.money >= total;
          const locked = it.unlocked === false;
          return (
            <div
              key={it.item_id}
              className={`panel p-3 flex items-start gap-3 ${locked ? "opacity-60" : ""}`}
              data-testid={`shop-item-${it.item_id}`}
            >
              <img src={it.sprite} alt={it.name} className={`pixelated w-12 h-12 shrink-0 ${locked ? "grayscale" : ""}`}/>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-baseline">
                  <h3 className="font-serif-display text-xl leading-none">
                    {it.name}
                    {it.capture_mult && it.capture_mult > 1 && (
                      <span className="ml-2 font-mono text-[10px] text-[#B87333]">×{it.capture_mult}</span>
                    )}
                  </h3>
                  <span className="font-mono text-sm">{it.price} ₽</span>
                </div>
                <p className="text-[11px] text-[#4A4A4A] mt-1 leading-tight">{it.description}</p>
                {locked ? (
                  <div className="mt-2 flex items-center gap-1 font-mono text-[10px] text-[#8C2A2A]">
                    <Lock size={11}/> Déblocage à {it.unlock_trainers} dresseurs recrutés
                    {shop.trainers_count !== undefined && (
                      <span className="ml-2 text-[#7A7A7A]">({shop.trainers_count}/{it.unlock_trainers})</span>
                    )}
                  </div>
                ) : (
                  <div className="flex gap-1 mt-2 items-center">
                    <input
                      type="number" min="1" max="99"
                      data-testid={`shop-qty-${it.item_id}`}
                      className="ink-input w-16 text-[11px] py-1"
                      value={qty}
                      onChange={(e) => setQtys({ ...qtys, [it.item_id]: Math.max(1, Math.min(99, parseInt(e.target.value) || 1)) })}
                    />
                    <span className="font-mono text-[10px] text-[#4A4A4A]">= {total} ₽</span>
                    <button
                      data-testid={`shop-buy-${it.item_id}`}
                      disabled={!canAfford || busy}
                      onClick={() => buy(it.item_id)}
                      className="btn-ink btn-leaf text-[10px] py-1 px-2 ml-auto"
                    >
                      <ShoppingCart size={10}/> Acheter
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
